import type { DisplayMode } from '@/types/book';

const READER_RESIDENT_PAGE_LIMIT = 20;

function addValidPageIndex(indexes: Set<number>, index: number, pageCount: number): void {
  if (index < 0 || index >= pageCount) return;
  indexes.add(index);
}

function normalizePageIndex(index: number, pageCount: number): number {
  return ((index % pageCount) + pageCount) % pageCount;
}

function addCircularPageIndex(indexes: Set<number>, index: number, pageCount: number): void {
  if (pageCount <= 0) return;
  indexes.add(normalizePageIndex(index, pageCount));
}

export function getSpreadPageIndexes(
  currentPageIndex: number,
  displayMode: DisplayMode,
  pageCount: number
): number[] {
  const indexes = new Set<number>();

  if (currentPageIndex < 0 || currentPageIndex >= pageCount || pageCount <= 0) return [];

  addValidPageIndex(indexes, currentPageIndex, pageCount);
  if (displayMode === 'double') {
    addCircularPageIndex(indexes, currentPageIndex + 1, pageCount);
  }

  return Array.from(indexes);
}

export function getVisibleWindowPageIndexes(
  currentPageIndex: number,
  displayMode: DisplayMode,
  pageCount: number
): number[] {
  if (pageCount <= 0) return [];
  const windowStartIndex =
    displayMode === 'double' && currentPageIndex === -1 ? 0 : currentPageIndex;
  if (windowStartIndex < 0 || windowStartIndex >= pageCount) return [];

  const indexes = new Set<number>();

  for (const index of getSpreadPageIndexes(windowStartIndex, displayMode, pageCount)) {
    indexes.add(index);
  }

  const forwardStartOffset = displayMode === 'double' ? 2 : 1;
  addCircularPageIndex(indexes, windowStartIndex + forwardStartOffset, pageCount);
  addCircularPageIndex(indexes, windowStartIndex + forwardStartOffset + 1, pageCount);
  addCircularPageIndex(indexes, windowStartIndex - 2, pageCount);
  addCircularPageIndex(indexes, windowStartIndex - 1, pageCount);

  return Array.from(indexes);
}

/**
 * 阅读端当前逻辑跨页对应的物理页面。
 * 双页封面态使用 -1 哨兵，仅对应物理封面 0；普通跨页不在末尾回卷封面。
 */
export function getReaderSpreadPageIndexes(
  currentPageIndex: number,
  displayMode: DisplayMode,
  pageCount: number
): number[] {
  if (pageCount <= 0) return [];
  if (displayMode === 'single') {
    return getSpreadPageIndexes(currentPageIndex, displayMode, pageCount);
  }
  if (currentPageIndex === -1) return [0];
  if (currentPageIndex < 0 || currentPageIndex >= pageCount) return [];

  const indexes = [currentPageIndex];
  if (currentPageIndex + 1 < pageCount) indexes.push(currentPageIndex + 1);
  return indexes;
}

/**
 * 阅读端常驻窗口：当前跨页优先，随后按下一、上一交替向两侧扩展。
 * 最多常驻 20 个物理页面；同一距离向后优先。
 */
export function getReaderResidentPageIndexes(
  currentPageIndex: number,
  displayMode: DisplayMode,
  pageCount: number
): number[] {
  if (pageCount <= 0) return [];

  const indexes = new Set<number>();
  const addSpread = (spreadStart: number) => {
    for (const index of getReaderSpreadPageIndexes(spreadStart, displayMode, pageCount)) {
      if (indexes.size >= READER_RESIDENT_PAGE_LIMIT) break;
      indexes.add(index);
    }
  };

  if (displayMode === 'single') {
    if (currentPageIndex < 0 || currentPageIndex >= pageCount) return [];

    addSpread(currentPageIndex);
    for (
      let distance = 1;
      distance < pageCount && indexes.size < READER_RESIDENT_PAGE_LIMIT;
      distance += 1
    ) {
      addSpread(currentPageIndex + distance);
      if (indexes.size < READER_RESIDENT_PAGE_LIMIT) {
        addSpread(currentPageIndex - distance);
      }
    }

    return Array.from(indexes);
  }

  const logicalSpreadStarts = [-1];
  for (let index = 1; index < pageCount; index += 2) {
    logicalSpreadStarts.push(index);
  }

  const currentSpreadPosition = logicalSpreadStarts.indexOf(currentPageIndex);
  if (currentSpreadPosition < 0) return [];

  addSpread(logicalSpreadStarts[currentSpreadPosition]);
  for (
    let distance = 1;
    distance < logicalSpreadStarts.length && indexes.size < READER_RESIDENT_PAGE_LIMIT;
    distance += 1
  ) {
    const nextPosition = (currentSpreadPosition + distance) % logicalSpreadStarts.length;
    addSpread(logicalSpreadStarts[nextPosition]);
    if (indexes.size >= READER_RESIDENT_PAGE_LIMIT) break;

    const previousPosition =
      (currentSpreadPosition - distance + logicalSpreadStarts.length) %
      logicalSpreadStarts.length;
    if (previousPosition !== nextPosition) {
      addSpread(logicalSpreadStarts[previousPosition]);
    }
  }

  return Array.from(indexes);
}
